import { isFunction } from '@agilepoint/utils-common';
import { MouseEvent, useCallback, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { IRatingProps } from '../types';
import { FlexBox } from '../../flex-box';
import { Icon } from '../../icon';
import { clamp } from '../../../utils';

const FilledStarContainer = styled.span <{ $isSelected?: boolean, $fill: number }>`
    display: block;
    position: absolute;
    left: 0;
    right: ${({ $fill }) => 100 - ($fill * 100)}%;
    top: 0;
    bottom: 0;
    overflow: hidden;
    z-index: ${({ $isSelected }) => $isSelected ? 1 : 0};
`;

const OutlinedIcon = styled(Icon) <{ color?: string }>`
    fill: ${({ theme: { rating: { colors } }, color }) => color ?? colors.ratingBorder};
`;

const FilledIcon = styled(Icon) <{ color?: string }>`
    position: absolute;
    left: 0;
    top: 0;
    fill: ${({ theme: { rating: { colors } }, color }) => color ?? colors.ratingFill};
`;

interface IStarProps extends Required<Pick<IRatingProps, 'step' | 'value' | 'size' | 'onChange'>> {
    className?: string;
    index: number;
    readonly?: boolean;
    borderColor?: string;
    backgroundColor?: string;
}

function StarBase(props: IStarProps) {
    const { className, value, index, step, size, readonly, borderColor, backgroundColor, onChange } = props;
    const fillValue = clamp(value - index > 1 ? 1 : value - index, 0, 1);
    const containerRef = useRef<HTMLDivElement>(null);
    const { rating: { composites: { iconSize } } } = useTheme();

    const onClick = useCallback((ev: MouseEvent) => {
        ev.stopPropagation();
        const { current } = containerRef;
        if (readonly || current === null || !isFunction(onChange)) {
            return;
        }
        const { x: startPositionX, width } = current.getBoundingClientRect();
        const position = (ev.clientX - startPositionX) / width;
        const stepNum = Number(step);
        const ceilValue = Math.ceil(position / stepNum) * stepNum;
        const floorValue = Math.floor(position / stepNum) * stepNum;
        const newValue = index + (ceilValue === (value - index) ? floorValue : ceilValue);
        onChange(newValue);
    }, [index, onChange, readonly, step, value]);

    return (
        <FlexBox ref={containerRef} className={className} inline onClick={onClick}>
            <OutlinedIcon iconName="ap-star.outline" iconSize={iconSize[size]} color={borderColor} />
            <FilledStarContainer $isSelected $fill={fillValue}>
                <FilledIcon iconName="ap-star.solid" iconSize={iconSize[size]} color={backgroundColor} />
            </FilledStarContainer>
        </FlexBox>
    );
}

export const Star = styled(StarBase).attrs({
    className: String('ap-starRating-star')
})`
    position: relative;
    flex-grow: 0;
    flex-shrink: 0;
    cursor: ${({ readonly }) => readonly ? 'default' : 'pointer'};
`;